// =====================================================================================
// FILE: src/components/modal/AddModal.jsx
// =====================================================================================
// Add New Content Modal - shows relevant fields based on table mode
// Excludes: Lumen8 Comments, Content Status (these are Airtable-managed)
// =====================================================================================

import React, { useState, useEffect, useRef, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { THEME } from "../../constants/theme";
import { filterAssetsByColumn } from "../../constants/assetDestinations";

// ============================================================================
// STANDALONE ASSET FIELD COMPONENT (moved outside to prevent re-render issues)
// ============================================================================
function AssetFieldPicker({ 
  label: fieldLabel, 
  assets = [], 
  value, 
  onChange,
  fieldGroupStyle,
  labelStyle,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  
  // Get selected asset details for thumbnail display
  const selectedAsset = assets.find(a => a.id === value);
  
  // Get thumbnail URL from asset
  const getThumbnail = (asset) => {
    if (!asset?.thumb) return null;
    if (Array.isArray(asset.thumb)) {
      const first = asset.thumb[0];
      if (first?.thumbnails?.small?.url) return first.thumbnails.small.url;
      if (first?.url) return first.url;
      if (typeof first === "string") return first;
    }
    if (typeof asset.thumb === "string") return asset.thumb.split(/[,\n]/)[0].trim();
    return null;
  };
  
  // Filter assets by search
  const filteredAssets = useMemo(() => {
    if (!search.trim()) return assets;
    const lower = search.toLowerCase();
    return assets.filter(a => 
      (a.title || "").toLowerCase().includes(lower) ||
      (a.destination || "").toLowerCase().includes(lower)
    );
  }, [assets, search]);
  
  // Calculate dropdown position when opening
  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 400;
      const spaceBelow = viewportHeight - rect.bottom - 10;
      const spaceAbove = rect.top - 10;
      
      // Determine if dropdown should open upward
      const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      
      setDropdownPos({
        top: openUpward ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 350),
        openUpward,
      });
    }
  }, [isOpen]);
  
  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);
  
  const thumbUrl = selectedAsset ? getThumbnail(selectedAsset) : null;
  
  // Handle selecting an asset
  const handleSelect = (assetId) => {
    onChange(assetId);
    setIsOpen(false);
    setSearch("");
  };
  
  // Dropdown content rendered via portal
  const dropdownContent = isOpen ? createPortal(
    <div
      ref={dropdownRef}
      data-asset-dropdown="true"
      style={{
        position: "fixed",
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        background: THEME.surface,
        border: `1px solid ${THEME.line}`,
        borderRadius: 12,
        boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        zIndex: 99999,
        maxHeight: 400,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header with search and close button */}
      <div style={{ 
        padding: "10px 12px", 
        borderBottom: `1px solid ${THEME.line}`,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}>
        <input
          type="text"
          placeholder="Search assets..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          style={{
            flex: 1,
            padding: "8px 10px",
            border: `1px solid ${THEME.line}`,
            borderRadius: 8,
            fontSize: 13,
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        <button
          onClick={() => { setIsOpen(false); setSearch(""); }}
          style={{
            background: THEME.chip,
            border: "none",
            borderRadius: 6,
            padding: "8px 12px",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
            color: THEME.textSecondary,
          }}
        >
          Close
        </button>
      </div>
      
      {/* Clear option */}
      {value && (
        <div
          onClick={() => handleSelect(null)}
          style={{
            padding: "10px 12px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
            color: THEME.error,
            fontWeight: 500,
            fontSize: 13,
            borderBottom: `1px solid ${THEME.line}`,
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,0,0,0.05)"}
          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
        >
          ✕ Clear selection
        </div>
      )}
      
      {/* Asset list */}
      <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
        {filteredAssets.length === 0 ? (
          <div style={{ padding: "20px 12px", textAlign: "center", color: THEME.textSecondary, fontSize: 13 }}>
            No assets found
          </div>
        ) : (
          filteredAssets.map((asset) => {
            const assetThumb = getThumbnail(asset);
            const isSelected = asset.id === value;
            return (
              <div
                key={asset.id}
                onClick={() => handleSelect(asset.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 12px",
                  cursor: "pointer",
                  background: isSelected ? "rgba(47, 214, 181, 0.1)" : "transparent",
                  borderLeft: isSelected ? "3px solid #2fd6b5" : "3px solid transparent",
                }}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = THEME.chip; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
              >
                {/* Thumbnail */}
                {assetThumb ? (
                  <img
                    src={assetThumb}
                    alt=""
                    style={{
                      width: 48,
                      height: 48,
                      objectFit: "contain",
                      borderRadius: 6,
                      background: THEME.chip,
                      flexShrink: 0,
                    }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div style={{
                    width: 48,
                    height: 48,
                    background: THEME.chip,
                    borderRadius: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    color: THEME.textSecondary,
                    flexShrink: 0,
                  }}>
                    🖼️
                  </div>
                )}
                
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: THEME.text,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    {asset.title || "(Untitled)"}
                  </div>
                  <div style={{ fontSize: 11, color: THEME.textSecondary, marginTop: 2 }}>
                    {asset.destination || "—"}
                  </div>
                </div>
                
                {/* Selected indicator */}
                {isSelected && (
                  <span style={{ color: "#2fd6b5", fontSize: 16 }}>✓</span>
                )}
              </div>
            );
          })
        )}
      </div>
      
      {/* Asset count */}
      <div style={{ 
        padding: "8px 12px", 
        borderTop: `1px solid ${THEME.line}`,
        fontSize: 11, 
        color: THEME.textSecondary,
        textAlign: "center",
      }}>
        {filteredAssets.length} asset{filteredAssets.length !== 1 ? "s" : ""} available
      </div>
    </div>,
    document.body
  ) : null;
  
  return (
    <div style={fieldGroupStyle}>
      <label style={labelStyle}>{fieldLabel}</label>
      <div style={{ position: "relative" }}>
        {/* Selected value display / trigger button */}
        <div
          ref={triggerRef}
          onClick={() => setIsOpen(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 12px",
            background: THEME.surface,
            border: `1px solid ${isOpen ? THEME.primary : THEME.line}`,
            borderRadius: 12,
            cursor: "pointer",
            transition: "border-color 0.15s",
          }}
        >
          {/* Thumbnail preview */}
          {thumbUrl ? (
            <img 
              src={thumbUrl} 
              alt="" 
              style={{ 
                width: 40, 
                height: 40, 
                objectFit: "contain", 
                borderRadius: 6,
                background: THEME.chip,
                flexShrink: 0,
              }} 
            />
          ) : (
            <div style={{
              width: 40,
              height: 40,
              background: THEME.chip,
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
              color: THEME.textSecondary,
              flexShrink: 0,
            }}>
              🖼️
            </div>
          )}
          
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ 
              fontWeight: 600, 
              color: selectedAsset ? THEME.text : THEME.textSecondary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}>
              {selectedAsset?.title || "-- Select Asset --"}
            </div>
            {selectedAsset?.destination && (
              <div style={{ fontSize: 11, color: THEME.textSecondary, marginTop: 2 }}>
                {selectedAsset.destination}
              </div>
            )}
          </div>
          
          <span style={{ color: THEME.textSecondary, fontSize: 12 }}>
            {isOpen ? "▲" : "▼"}
          </span>
        </div>
        
        {/* Portal dropdown */}
        {dropdownContent}
      </div>
      {assets.length === 0 && (
        <div style={{ fontSize: 12, color: THEME.textSecondary, marginTop: 4 }}>
          No assets available for this field
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function AddModal({
  initialCategory,
  categories = [], // ✅ List of available categories
  onClose,
  onCreate,
  isLedMode = false,
  isIptvMode = false,
  isTeamMode = false,
  isInGameMode = false,
  venue = "ALL", // MCG, MRV, or ALL
  iptvOptions = {},
  assetCache,
}) {
  const modalRef = useRef();

  // ============================================================================
  // FORM STATE
  // ============================================================================
  const [formData, setFormData] = useState({
    category: initialCategory || (categories.length > 0 ? categories[0] : ""), // ✅ Category is now editable
    name: "",
    clientSponsorName: "",
    clubInstructions: "",
    // LED specific
    allocationId: "",
    // IPTV / LED Commercial specific
    rotations: null,
    bonusPriority: null,
    selectTiming: [],
    selectLocation: [],
    // Team List specific
    playerNumber: "",
    // Asset fields (stored as IDs)
    iptvGfxId: null,
    bigGfxVideosId: null,
    cornerGfxVideosId: null,
    ledAllTypesId: null,
    bigTvcId: null,
    cornerTvcId: null,
    superFullId: null,
    super169Id: null,
    superSideId: null,
    mcgTiledLedId: null,
    marvelTiledLedId: null,
    mcgInterchangeId: null,
    marvelInterchangeId: null,
    mcg360Id: null,
    marvel360Id: null,
  });

  const [saving, setSaving] = useState(false);

  // ============================================================================
  // CLICK OUTSIDE TO CLOSE (ignores portal dropdowns)
  // ============================================================================
  useEffect(() => {
    function handleClickOutside(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        // Don't close if clicking inside a portaled dropdown (e.g. asset picker)
        const portalDropdown = e.target.closest('[data-asset-dropdown]');
        if (portalDropdown) return;
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // ============================================================================
  // ESCAPE TO CLOSE
  // ============================================================================
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // ============================================================================
  // HANDLERS
  // ============================================================================
  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onCreate(formData);
    } catch (e) {
      console.error("Create failed:", e);
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // ASSET HELPERS
  // ============================================================================
  const getAssetsForColumn = (columnKey) => {
    if (!assetCache) return [];
    const allAssets = assetCache instanceof Map 
      ? Array.from(assetCache.values()) 
      : Object.values(assetCache);
    return filterAssetsByColumn(allAssets, columnKey);
  };

  // ============================================================================
  // STYLES
  // ============================================================================
  const overlay = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    padding: 20,
  };

  const modal = {
    background: "white",
    borderRadius: 18,
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    maxWidth: 600,
    width: "100%",
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const header = {
    padding: "20px 24px",
    borderBottom: `1px solid ${THEME.line}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const body = {
    padding: "20px 24px",
    overflowY: "auto",
    flex: 1,
  };

  const footer = {
    padding: "16px 24px",
    borderTop: `1px solid ${THEME.line}`,
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
  };

  const fieldGroup = {
    marginBottom: 20,
  };

  const label = {
    display: "block",
    fontSize: 13,
    fontWeight: 900,
    color: THEME.text,
    marginBottom: 6,
  };

  const input = {
    width: "100%",
    padding: "10px 12px",
    border: `1px solid ${THEME.line}`,
    borderRadius: 10,
    fontSize: 14,
    boxSizing: "border-box",
  };

  const textarea = {
    ...input,
    minHeight: 80,
    resize: "vertical",
  };

  const select = {
    ...input,
    cursor: "pointer",
  };

  const btnCancel = {
    padding: "10px 20px",
    border: `1px solid ${THEME.line}`,
    background: "white",
    color: THEME.text,
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 900,
    cursor: "pointer",
  };

  const btnSubmit = {
    padding: "10px 20px",
    border: `1px solid ${THEME.primary}`,
    background: THEME.primary,
    color: "white",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 900,
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
  };

  // ============================================================================
  // DETERMINE WHICH FIELDS TO SHOW
  // ============================================================================
  
  // Check if we should show In-Game Commercial fields (LED table renamed)
  const isInGameCommercial = isLedMode;
  
  // Determine which asset columns to show based on mode and venue
  const showBigGfxVideos = !isIptvMode && !isInGameMode && !isInGameCommercial && (venue === "MCG" || venue === "ALL");
  const showCornerGfxVideos = !isIptvMode && !isInGameMode && !isInGameCommercial && (venue === "MRV" || venue === "ALL");
  const showLedAllTypes = !isIptvMode && !isInGameMode;
  const showBigTvc = isInGameMode && (venue === "MCG" || venue === "ALL");
  const showCornerTvc = isInGameMode && (venue === "MRV" || venue === "ALL");
  // SuperScreens only for Game Timeline (Out of Game) - NOT for TVC, LED, Team List, or IPTV
  const showSuperScreens = !isIptvMode && !isInGameMode && !isInGameCommercial && !isTeamMode && (venue === "MRV" || venue === "ALL");
  const showIptvGfx = isIptvMode;
  const showRotations = isIptvMode || isLedMode; // ✅ Show for IPTV AND In-Game LED Commercial
  const showBonusPriority = isLedMode; // ✅ Show Bonus Priority for LED only
  const showAllocationId = false; // ✅ Never show Allocation ID in AddModal
  const showTimingLocation = isIptvMode;
  const showPlayerNumber = isTeamMode; // ✅ Show Player Number for Team List

  // ============================================================================
  // ASSET FIELD HELPERS
  // ============================================================================
  // NOTE: Do NOT wrap AssetFieldPicker in an inline component here.
  // Defining const AssetField = (props) => <AssetFieldPicker .../> inside the
  // render body creates a new component type each render, causing React to
  // unmount/remount the picker and destroy its internal state (search, etc.).

  // ============================================================================
  // RENDER MULTI-SELECT FIELD (for IPTV timing/location)
  // ============================================================================
  const MultiSelectField = ({ label: fieldLabel, options = [], value = [], onChange }) => {
    const toggleOption = (opt) => {
      const newValue = value.includes(opt)
        ? value.filter((v) => v !== opt)
        : [...value, opt];
      onChange(newValue);
    };

    return (
      <div style={fieldGroup}>
        <label style={label}>{fieldLabel}</label>
        <div style={{ 
          border: `1px solid ${THEME.line}`, 
          borderRadius: 10, 
          padding: 10,
          maxHeight: 150,
          overflowY: "auto",
        }}>
          {options.length === 0 ? (
            <div style={{ fontSize: 13, color: THEME.textSecondary }}>No options available</div>
          ) : (
            options.map((opt) => (
              <label
                key={opt}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 4px",
                  cursor: "pointer",
                  borderRadius: 6,
                  background: value.includes(opt) ? THEME.chip : "transparent",
                }}
              >
                <input
                  type="checkbox"
                  checked={value.includes(opt)}
                  onChange={() => toggleOption(opt)}
                />
                <span style={{ fontSize: 13, fontWeight: 700, color: THEME.text }}>{opt}</span>
              </label>
            ))
          )}
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  return (
    <div style={overlay}>
      <div ref={modalRef} style={modal}>
        {/* Header */}
        <div style={header}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 1000, color: THEME.text }}>
            + New Content Row
          </h2>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 24,
              cursor: "pointer",
              color: THEME.textSecondary,
              lineHeight: 1,
            }}
            title="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={body}>
          {/* Category (dropdown) */}
          <div style={fieldGroup}>
            <label style={label}>Category *</label>
            <select
              style={select}
              value={formData.category}
              onChange={(e) => updateField("category", e.target.value)}
            >
              {categories.length === 0 ? (
                <option value="">No categories available</option>
              ) : (
                categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Name */}
          <div style={fieldGroup}>
            <label style={label}>Name *</label>
            <input
              type="text"
              style={input}
              value={formData.name}
              onChange={(e) => updateField("name", e.target.value)}
              placeholder="Enter content name..."
              autoFocus
            />
          </div>

          {/* Client / Sponsor Name */}
          <div style={fieldGroup}>
            <label style={label}>Client or Sponsor Name</label>
            <input
              type="text"
              style={input}
              value={formData.clientSponsorName}
              onChange={(e) => updateField("clientSponsorName", e.target.value)}
              placeholder="Enter client or sponsor name..."
            />
          </div>

          {/* Club Instructions */}
          <div style={fieldGroup}>
            <label style={label}>Club Instructions</label>
            <textarea
              style={textarea}
              value={formData.clubInstructions}
              onChange={(e) => updateField("clubInstructions", e.target.value)}
              placeholder="Enter any special instructions..."
            />
          </div>

          {/* ============ TEAM LIST SPECIFIC FIELDS ============ */}
          {showPlayerNumber && (
            <div style={fieldGroup}>
              <label style={label}>Player Number</label>
              <input
                type="text"
                style={input}
                value={formData.playerNumber}
                onChange={(e) => updateField("playerNumber", e.target.value)}
                placeholder="Enter player number..."
              />
            </div>
          )}

          {/* ============ IPTV SPECIFIC FIELDS ============ */}
          {showIptvGfx && (
            <AssetFieldPicker
              label="IPTV GFX"
              assets={getAssetsForColumn("iptvGfx")}
              value={formData.iptvGfxId}
              onChange={(v) => updateField("iptvGfxId", v)}
              fieldGroupStyle={fieldGroup}
              labelStyle={label}
            />
          )}

          {showRotations && (
            <div style={fieldGroup}>
              <label style={label}>Rotations</label>
              <input
                type="text"
                style={input}
                value={formData.rotations || ""}
                onChange={(e) => updateField("rotations", e.target.value || null)}
                placeholder="Enter rotation value..."
              />
            </div>
          )}

          {showBonusPriority && (
            <div style={fieldGroup}>
              <label style={label}>Bonus Priority</label>
              <input
                type="number"
                style={input}
                value={formData.bonusPriority || ""}
                onChange={(e) => updateField("bonusPriority", e.target.value ? parseInt(e.target.value) : null)}
                placeholder="1-10 (1 = highest priority)"
                min={1}
                max={10}
              />
            </div>
          )}

          {showTimingLocation && (
            <>
              <MultiSelectField
                label="Select Timing"
                options={iptvOptions?.timing || []}
                value={formData.selectTiming}
                onChange={(v) => updateField("selectTiming", v)}
              />
              <MultiSelectField
                label="Select Location"
                options={iptvOptions?.location || []}
                value={formData.selectLocation}
                onChange={(v) => updateField("selectLocation", v)}
              />
            </>
          )}

          {/* ============ LED / IN-GAME COMMERCIAL SPECIFIC FIELDS ============ */}
          {showAllocationId && (
            <div style={fieldGroup}>
              <label style={label}>Allocation ID</label>
              <input
                type="text"
                style={input}
                value={formData.allocationId}
                onChange={(e) => updateField("allocationId", e.target.value)}
                placeholder="Enter allocation ID..."
              />
            </div>
          )}

          {showLedAllTypes && (
            <AssetFieldPicker
              label="LED - All Types"
              assets={getAssetsForColumn("ledAllTypes")}
              value={formData.ledAllTypesId}
              onChange={(v) => updateField("ledAllTypesId", v)}
              fieldGroupStyle={fieldGroup}
              labelStyle={label}
            />
          )}

          {/* ============ TVC CONTENT SPECIFIC FIELDS ============ */}
          {showBigTvc && (
            <AssetFieldPicker
              label="Big Screen - TVC"
              assets={getAssetsForColumn("bigTvc")}
              value={formData.bigTvcId}
              onChange={(v) => updateField("bigTvcId", v)}
              fieldGroupStyle={fieldGroup}
              labelStyle={label}
            />
          )}

          {showCornerTvc && (
            <AssetFieldPicker
              label="Corner Screen - TVC"
              assets={getAssetsForColumn("cornerTvc")}
              value={formData.cornerTvcId}
              onChange={(v) => updateField("cornerTvcId", v)}
              fieldGroupStyle={fieldGroup}
              labelStyle={label}
            />
          )}

          {/* ============ GAME TIMELINE / OUT OF GAME SPECIFIC FIELDS ============ */}
          {showBigGfxVideos && (
            <AssetFieldPicker
              label="Big Screen - GFX & Videos"
              assets={getAssetsForColumn("bigGfxVideos")}
              value={formData.bigGfxVideosId}
              onChange={(v) => updateField("bigGfxVideosId", v)}
              fieldGroupStyle={fieldGroup}
              labelStyle={label}
            />
          )}

          {showCornerGfxVideos && (
            <AssetFieldPicker
              label="Corner Screen - GFX & Videos"
              assets={getAssetsForColumn("cornerGfxVideos")}
              value={formData.cornerGfxVideosId}
              onChange={(v) => updateField("cornerGfxVideosId", v)}
              fieldGroupStyle={fieldGroup}
              labelStyle={label}
            />
          )}

          {/* ============ SUPERSCREEN FIELDS (MARVEL ONLY) ============ */}
          {showSuperScreens && (
            <>
              <AssetFieldPicker
                label="SuperScreen - Fullscreen / Takeovers"
                assets={getAssetsForColumn("superFull")}
                value={formData.superFullId}
                onChange={(v) => updateField("superFullId", v)}
                fieldGroupStyle={fieldGroup}
                labelStyle={label}
              />
              <AssetFieldPicker
                label="SuperScreen - 20:9 Safe Graphics"
                assets={getAssetsForColumn("super169")}
                value={formData.super169Id}
                onChange={(v) => updateField("super169Id", v)}
                fieldGroupStyle={fieldGroup}
                labelStyle={label}
              />
              <AssetFieldPicker
                label="SuperScreen - Side Panels"
                assets={getAssetsForColumn("superSide")}
                value={formData.superSideId}
                onChange={(v) => updateField("superSideId", v)}
                fieldGroupStyle={fieldGroup}
                labelStyle={label}
              />
            </>
          )}
        </div>

        {/* Footer */}
        <div style={footer}>
          <button type="button" style={btnCancel} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            style={btnSubmit}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Creating..." : "Create Content"}
          </button>
        </div>
      </div>
    </div>
  );
}
